import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Modal, Table } from '@heroui/react';
import type { LocaleCode } from '@/api/models';
import { DatePickerField } from '@/components/datePickerField';
import { JournalEntryModal } from '@/pages/accounting/journalEntry';
import { postBlockedReason } from '@/pages/accounting/journalEntry/journalEntryMeta';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatPeriod, rateLabel } from '../../salariesMeta';
import { usePayrollRun } from './usePayrollRun';

type PayrollRunDialogProps = {
    isOpen: boolean;
    locale: LocaleCode;
    onClose: () => void;
    onOpenExistingRun: (runId: string) => void;
};

/**
 * Run payroll for a month.
 *
 * Shows every employee's computed gross, tax, employee pension, employer pension
 * and net with column totals, then builds **one** balanced draft entry — the
 * period's payroll grouped by account — and hands it to the Journal Entry form.
 * Nothing posts automatically.
 *
 * Four things on this dialog are load-bearing:
 *
 * - The **entry shape** is stated under the totals: gross and employer pension
 *   are the only debits. A user who can see that can catch the one mistake this
 *   screen exists to prevent.
 * - The **state contribution** is shown in its own de-emphasised column with an
 *   explicit "not part of the entry" note. It never touches these books.
 * - The **proration convention** (calendar days) is stated in the dialog itself,
 *   the way the fixed-asset dialog states its full-month convention.
 * - The **rates** in the column headers are the ones in force for *this*
 *   period, resolved from the effective-dated rate set — not constants. A month
 *   run after a rate change still shows the rates it was subject to, and a
 *   period with no set in force shows no percentages at all rather than
 *   borrowing today's.
 *
 * Employees who contribute nothing appear as an en dash with the reason spelled
 * out beside them, because a zero the user cannot explain is indistinguishable
 * from a bug. A period that has already been run is **warned about, not
 * blocked**: the commit is idempotent per period and hands back the run that
 * exists, so the honest affordance is to say so and let the user open it.
 */
export const PayrollRunDialog: React.FC<PayrollRunDialogProps> = ({
    isOpen,
    locale,
    onClose,
    onOpenExistingRun,
}) => {
    const { t } = useTranslation('pages');
    const model = usePayrollRun({ locale, onClose, onOpenExistingRun });
    const alreadyRun = model.existingRun !== null;
    // The server's own verdict on the draft it would build — it already applies
    // the accounting-period gate, so nothing here recomputes postability.
    const blockedReason = model.previewValidation
        ? postBlockedReason(model.previewValidation, t)
        : null;

    return (
        <>
            <Modal
                isOpen={isOpen && model.draft === null}
                onOpenChange={(open) => !open && onClose()}
            >
                <Modal.Backdrop>
                    <Modal.Container size="lg">
                        <Modal.Dialog
                            className="w-full max-w-4xl"
                            {...getNodeTestIdProp('salaries-run-dialog')}
                        >
                            <Modal.CloseTrigger />
                            <Modal.Header>
                                <Modal.Heading>
                                    {t('salaries.run.title')}
                                </Modal.Heading>
                                <p className="text-sm text-muted">
                                    {t('salaries.run.subtitle')}
                                </p>
                            </Modal.Header>

                            <Modal.Body className="flex flex-col gap-4 pb-2">
                                <div className="flex flex-wrap items-end gap-4">
                                    <div className="w-full max-w-[15rem]">
                                        <DatePickerField
                                            id="salaries-run-period"
                                            label={t(
                                                'salaries.run.periodLabel',
                                            )}
                                            name="period"
                                            onChange={model.onPeriodDateChange}
                                            value={model.periodDate}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 pb-1">
                                        <span className="text-xs font-medium uppercase tracking-wide text-muted">
                                            {t('salaries.run.periodCovered')}
                                        </span>
                                        <span
                                            className="text-sm font-semibold text-foreground"
                                            {...getNodeTestIdProp(
                                                'salaries-run-period-label',
                                            )}
                                        >
                                            {formatPeriod(model.period, t)}
                                        </span>
                                    </div>
                                    <Button
                                        isPending={model.isPreviewing}
                                        onPress={model.onPreview}
                                        variant="outline"
                                        {...getNodeTestIdProp(
                                            'salaries-run-preview',
                                        )}
                                    >
                                        {t('salaries.run.preview')}
                                    </Button>
                                </div>

                                <p className="text-xs text-muted">
                                    {t('salaries.run.prorationNote')}
                                </p>

                                {alreadyRun ? (
                                    <div
                                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-warning-soft px-3 py-2"
                                        {...getNodeTestIdProp(
                                            'salaries-run-existing',
                                        )}
                                    >
                                        <span className="flex items-center gap-2 text-sm text-warning-soft-foreground">
                                            <Icon
                                                aria-hidden
                                                icon="gravity-ui:circle-info"
                                            />
                                            {t('salaries.run.alreadyRun', {
                                                period: formatPeriod(
                                                    model.period,
                                                    t,
                                                ),
                                            })}
                                        </span>
                                        <Button
                                            onPress={model.onOpenExisting}
                                            size="sm"
                                            variant="outline"
                                            {...getNodeTestIdProp(
                                                'salaries-run-open-existing',
                                            )}
                                        >
                                            {t('salaries.run.openExistingRun')}
                                        </Button>
                                    </div>
                                ) : null}

                                {model.error ? (
                                    <p
                                        className="text-sm text-danger"
                                        {...getNodeTestIdProp(
                                            'salaries-run-error',
                                        )}
                                    >
                                        {model.error}
                                    </p>
                                ) : null}

                                {model.hasPreview && model.totals ? (
                                    <>
                                        {/* `shrink-0` is load-bearing. This is a flex child of a
                                            `flex flex-col` modal body, and `overflow-hidden`
                                            resolves its `min-height: auto` to 0 — so without it
                                            the wrapper shrinks below the table and silently clips
                                            the totals row, with no scrollbar anywhere to reach it.
                                            Keeping its natural height hands the vertical scroll
                                            back to the modal body, where it belongs. */}
                                        <div className="shrink-0 overflow-hidden rounded-xl border border-separator">
                                            {/* Horizontal only. A `max-h` vertical
                                                scroller here would be a second
                                                scrollport inside the modal body's
                                                own, and the body clips it — the
                                                totals row ends up painted outside
                                                the visible area. The body owns the
                                                vertical scroll; the table renders
                                                whole, so its totals are always
                                                reachable. */}
                                            <div
                                                {...getNodeTestIdProp(
                                                    'salaries-run-table',
                                                )}
                                            >
                                                <Table>
                                                    <Table.ScrollContainer>
                                                        <Table.Content
                                                            aria-label={t(
                                                                'salaries.run.tableAria',
                                                            )}
                                                            className="min-w-[820px] text-sm"
                                                        >
                                                            <Table.Header>
                                                                <Table.Column
                                                                    id="name"
                                                                    isRowHeader
                                                                >
                                                                    {t(
                                                                        'salaries.columns.name',
                                                                    )}
                                                                </Table.Column>
                                                                <Table.Column id="gross">
                                                                    {t(
                                                                        'salaries.run.columns.gross',
                                                                    )}
                                                                </Table.Column>
                                                                <Table.Column id="incomeTax">
                                                                    {rateLabel(
                                                                        'salaries.run.columns.incomeTax',
                                                                        model
                                                                            .rates
                                                                            ?.incomeTax,
                                                                        t,
                                                                    )}
                                                                </Table.Column>
                                                                <Table.Column id="employeePension">
                                                                    {rateLabel(
                                                                        'salaries.run.columns.employeePension',
                                                                        model
                                                                            .rates
                                                                            ?.employeePension,
                                                                        t,
                                                                    )}
                                                                </Table.Column>
                                                                <Table.Column id="employerPension">
                                                                    {rateLabel(
                                                                        'salaries.run.columns.employerPension',
                                                                        model
                                                                            .rates
                                                                            ?.employerPension,
                                                                        t,
                                                                    )}
                                                                </Table.Column>
                                                                <Table.Column id="net">
                                                                    {t(
                                                                        'salaries.run.columns.net',
                                                                    )}
                                                                </Table.Column>
                                                                {/* Information only — visually
                                                                    separated from the five
                                                                    columns that reach the entry. */}
                                                                <Table.Column
                                                                    className="border-l border-separator"
                                                                    id="stateContribution"
                                                                >
                                                                    {rateLabel(
                                                                        'salaries.run.columns.stateContribution',
                                                                        model
                                                                            .rates
                                                                            ?.state,
                                                                        t,
                                                                    )}
                                                                </Table.Column>
                                                            </Table.Header>
                                                            <Table.Body>
                                                                {model.rows.map(
                                                                    (row) => (
                                                                        <Table.Row
                                                                            id={
                                                                                row.employeeId
                                                                            }
                                                                            key={
                                                                                row.employeeId
                                                                            }
                                                                            {...getNodeTestIdProp(
                                                                                `salaries-run-row-${row.employeeId}`,
                                                                            )}
                                                                        >
                                                                            <Table.Cell>
                                                                                <span className="flex flex-col">
                                                                                    <span className="text-foreground">
                                                                                        {
                                                                                            row.name
                                                                                        }
                                                                                    </span>
                                                                                    {row.reasonKey ? (
                                                                                        <span className="text-xs text-muted">
                                                                                            {t(
                                                                                                row.reasonKey,
                                                                                                row.prorationDays ??
                                                                                                    {},
                                                                                            )}
                                                                                        </span>
                                                                                    ) : null}
                                                                                </span>
                                                                            </Table.Cell>
                                                                            {[
                                                                                row.grossText,
                                                                                row.incomeTaxText,
                                                                                row.employeePensionText,
                                                                                row.employerPensionText,
                                                                                row.netText,
                                                                            ].map(
                                                                                (
                                                                                    value,
                                                                                    column,
                                                                                ) => (
                                                                                    <Table.Cell
                                                                                        key={
                                                                                            column
                                                                                        }
                                                                                    >
                                                                                        <span
                                                                                            className={cn(
                                                                                                'block whitespace-nowrap text-right tabular-nums',
                                                                                                row.isZero
                                                                                                    ? 'text-muted'
                                                                                                    : 'text-foreground',
                                                                                            )}
                                                                                        >
                                                                                            {
                                                                                                value
                                                                                            }
                                                                                        </span>
                                                                                    </Table.Cell>
                                                                                ),
                                                                            )}
                                                                            <Table.Cell className="border-l border-separator">
                                                                                <span className="block whitespace-nowrap text-right tabular-nums text-muted">
                                                                                    {
                                                                                        row.stateContributionText
                                                                                    }
                                                                                </span>
                                                                            </Table.Cell>
                                                                        </Table.Row>
                                                                    ),
                                                                )}
                                                                {/* The total is the last
                                                                    body row, not a
                                                                    `Table.Footer`: the
                                                                    footer slot renders
                                                                    outside the table, and
                                                                    the totals must stay
                                                                    column-aligned with the
                                                                    figures above them. */}
                                                                <Table.Row
                                                                    className="border-t-2 border-foreground/50 bg-surface-secondary"
                                                                    id="salaries-run-total-row"
                                                                    key="salaries-run-total-row"
                                                                >
                                                                    <Table.Cell>
                                                                        <span className="text-sm font-semibold">
                                                                            {t(
                                                                                'salaries.run.total',
                                                                            )}
                                                                        </span>
                                                                    </Table.Cell>
                                                                    {[
                                                                        model
                                                                            .totals
                                                                            .grossText,
                                                                        model
                                                                            .totals
                                                                            .incomeTaxText,
                                                                        model
                                                                            .totals
                                                                            .employeePensionText,
                                                                        model
                                                                            .totals
                                                                            .employerPensionText,
                                                                        model
                                                                            .totals
                                                                            .netText,
                                                                    ].map(
                                                                        (
                                                                            value,
                                                                            column,
                                                                        ) => (
                                                                            <Table.Cell
                                                                                key={
                                                                                    column
                                                                                }
                                                                                {...getNodeTestIdProp(
                                                                                    `salaries-run-total-${column}`,
                                                                                )}
                                                                            >
                                                                                <span className="block whitespace-nowrap text-right text-sm font-bold tabular-nums text-foreground">
                                                                                    {
                                                                                        value
                                                                                    }
                                                                                </span>
                                                                            </Table.Cell>
                                                                        ),
                                                                    )}
                                                                    <Table.Cell className="border-l border-separator">
                                                                        <span className="block whitespace-nowrap text-right text-sm font-medium tabular-nums text-muted">
                                                                            {
                                                                                model
                                                                                    .totals
                                                                                    .stateContributionText
                                                                            }
                                                                        </span>
                                                                    </Table.Cell>
                                                                </Table.Row>
                                                            </Table.Body>
                                                        </Table.Content>
                                                    </Table.ScrollContainer>
                                                </Table>
                                            </div>
                                        </div>

                                        {model.totals.hasStateContribution ? (
                                            <p
                                                className="flex items-start gap-2 text-xs text-muted"
                                                {...getNodeTestIdProp(
                                                    'salaries-run-state-note',
                                                )}
                                            >
                                                <Icon
                                                    aria-hidden
                                                    className="mt-0.5 shrink-0"
                                                    icon="gravity-ui:circle-info"
                                                />
                                                {t('salaries.run.stateNote')}
                                            </p>
                                        ) : null}

                                        {/* The entry's shape, stated plainly: a
                                            user who can read this can catch a
                                            withholding that was debited. */}
                                        <div
                                            className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-surface-secondary px-3 py-2"
                                            {...getNodeTestIdProp(
                                                'salaries-run-entry-shape',
                                            )}
                                        >
                                            <span className="text-xs text-muted">
                                                {t('salaries.run.entryShape')}
                                            </span>
                                            <span className="flex items-baseline gap-2 text-sm">
                                                <span className="text-muted">
                                                    {t(
                                                        'salaries.run.entryTotal',
                                                    )}
                                                </span>
                                                <span
                                                    className="font-bold tabular-nums text-foreground"
                                                    {...getNodeTestIdProp(
                                                        'salaries-run-entry-total',
                                                    )}
                                                >
                                                    {
                                                        model.totals
                                                            .entryTotalText
                                                    }
                                                </span>
                                            </span>
                                        </div>
                                    </>
                                ) : null}

                                {/* The server's verdict on the draft it would
                                    build, period gate included. Rendered, not
                                    recomputed — and advisory: the entry is
                                    created either way, and the Journal Entry
                                    form is where the post is refused. */}
                                {blockedReason ? (
                                    <p
                                        className="flex items-start gap-2 text-xs text-warning-soft-foreground"
                                        {...getNodeTestIdProp(
                                            'salaries-run-not-postable',
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            className="mt-0.5 shrink-0"
                                            icon="gravity-ui:triangle-exclamation"
                                        />
                                        {t('salaries.run.notPostable', {
                                            reason: blockedReason,
                                        })}
                                    </p>
                                ) : null}

                                {model.isNilRun ? (
                                    <p
                                        className="flex items-center gap-2 text-sm text-muted"
                                        {...getNodeTestIdProp(
                                            'salaries-run-nil',
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            icon="gravity-ui:circle-info"
                                        />
                                        {t('salaries.run.nothingToPay')}
                                    </p>
                                ) : null}
                            </Modal.Body>

                            <Modal.Footer>
                                <Button
                                    onPress={onClose}
                                    variant="tertiary"
                                    {...getNodeTestIdProp(
                                        'salaries-run-cancel',
                                    )}
                                >
                                    {t('salaries.actions.cancel')}
                                </Button>
                                {/* Enabled for a period that has already been
                                    run: the commit is idempotent per period and
                                    returns the existing draft, so pressing this
                                    cannot charge the month twice. It is refused
                                    only when there is genuinely nothing to
                                    post. */}
                                <Button
                                    isDisabled={
                                        !model.hasPreview || model.isNilRun
                                    }
                                    isPending={model.isCommitting}
                                    onPress={model.onReview}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'salaries-run-review',
                                    )}
                                >
                                    {alreadyRun
                                        ? t('salaries.actions.openEntry')
                                        : t('salaries.actions.reviewEntry')}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal>

            {/* The real thing, fed the entry the commit persisted. Posting it
                here is what finally moves the register — nothing before this
                point has charged the period. */}
            <JournalEntryModal
                entry={model.draft}
                isOpen={model.draft !== null}
                onClose={model.onDraftClose}
                onSaved={model.onDraftSaved}
            />
        </>
    );
};
